/**
 * Sticky ATC mobile
 *
 * Behaviour:
 * - Reveals the bar once the user has scrolled past the main buy button.
 * - Hides it when the buy button or the footer is visible in the viewport,
 *   so we never show two CTAs at the same time.
 * - Updates the price + label on variant change to stay in sync with the form.
 *
 * Implementation: scroll listener throttled with requestAnimationFrame.
 * We deliberately avoid IntersectionObserver here because mobile momentum
 * scrolling can skip threshold crossings, leading to a stale state.
 */
class StickyAtcMobile extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('[data-sticky-atc-button]');
    this.label = this.querySelector('[data-sticky-atc-label]');
    this.priceContainer = this.querySelector('[data-sticky-atc-price]');
    this.sectionId = this.dataset.section;

    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    this.ticking = false;
    this.boundOnScroll = this.onScroll.bind(this);
    this.boundOnResize = this.onResize.bind(this);
    this.boundUpdate = this.updateVisibility.bind(this);
  }

  connectedCallback() {
    if (!this.mediaQuery.matches) return;
    this.removeAttribute('hidden');
    this.cacheRefs();
    this.attachListeners();
    this.subscribeToVariantChange();
    // Initial state: run after layout is settled.
    requestAnimationFrame(() => this.updateVisibility());
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundOnScroll);
    window.removeEventListener('resize', this.boundOnResize);
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.boundOnResize);
    }
    if (this.variantUnsubscriber) this.variantUnsubscriber();
  }

  cacheRefs() {
    const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
    this.buyButtonEl =
      (productInfo && productInfo.querySelector('product-form')) ||
      (productInfo && productInfo.querySelector('.product-form')) ||
      (productInfo && productInfo.querySelector('.product-form__buttons'));
  }

  attachListeners() {
    window.addEventListener('scroll', this.boundOnScroll, { passive: true });
    window.addEventListener('resize', this.boundOnResize, { passive: true });
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.boundOnResize);
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.boundUpdate();
      this.ticking = false;
    });
  }

  onResize() {
    // If we cross the breakpoint while the page is open, hide the bar entirely.
    if (!this.mediaQuery.matches) {
      this.setVisible(false);
      return;
    }
    // Refs may have shifted (e.g. orientation change on mobile).
    this.cacheRefs();
    this.updateVisibility();
  }

  updateVisibility() {
    if (!this.mediaQuery.matches) {
      this.setVisible(false);
      return;
    }

    // Visible by default. Hide only while the main buy button is in viewport.
    if (this.buyButtonEl) {
      const rect = this.buyButtonEl.getBoundingClientRect();
      const buyButtonInView = rect.bottom > 0 && rect.top < window.innerHeight;
      this.setVisible(!buyButtonInView);
      return;
    }

    this.setVisible(true);
  }

  setVisible(visible) {
    if (visible) {
      this.setAttribute('data-visible', 'true');
    } else {
      this.removeAttribute('data-visible');
    }
  }

  subscribeToVariantChange() {
    if (!window.subscribe || !window.PUB_SUB_EVENTS) return;
    this.variantUnsubscriber = window.subscribe(
      window.PUB_SUB_EVENTS.variantChange,
      (event) => {
        if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
        const variant = event.data.variant;
        if (!variant) {
          this.setDisabled(true, 'Indisponible');
          return;
        }
        this.setDisabled(!variant.available, variant.available ? 'Ajouter' : 'Épuisé');
        this.updatePrice(variant);
      }
    );
  }

  setDisabled(disabled, labelText) {
    if (!this.button) return;
    this.button.disabled = disabled;
    if (labelText && this.label) this.label.textContent = labelText;
  }

  updatePrice(variant) {
    if (!this.priceContainer || !variant) return;
    const formatMoney = (value) => {
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(value, '{{amount}} €');
      }
      return `${(value / 100).toFixed(2).replace('.', ',')} €`;
    };

    const current = `<span class="sticky-atc__price-current">${formatMoney(variant.price)}</span>`;
    const compare =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<s class="sticky-atc__price-compare">${formatMoney(variant.compare_at_price)}</s>`
        : '';
    this.priceContainer.innerHTML = `${current}${compare}`;
  }
}

if (!customElements.get('sticky-atc-mobile')) {
  customElements.define('sticky-atc-mobile', StickyAtcMobile);
}
